<!--
  A GitHub label as a chip: the one way a label is drawn, on dashboard
  rows, in the pull request's header and beside its reviewers. Its colours
  are worked out from the label's own for each theme (`labelTones`): the
  label's colour with black or white text in light, a tint with the colour
  lightened in dark. A long name is cut, with the whole name and the
  label's description on hover; under the keyboard's focus it is shown
  whole. Given `onpick` it is a button (the dashboard filters by the
  label), and its click goes no further; otherwise it is plain text.
-->
<script lang="ts">
  import type { Label } from '../api/queries';
  import { labelTones } from '../logic/labelColor';

  interface Props {
    label: Label;
    /** What a click on it does, with its name. */
    onpick?: (name: string) => void;
    /** Whether it takes the Tab key (a button only). */
    tabbable?: boolean;
  }
  let { label, onpick, tabbable = true }: Props = $props();

  const tones = $derived(labelTones(label.color));
  const full = $derived(label.description ? `${label.name}: ${label.description}` : label.name);
  const vars = $derived(
    `--lb:${tones.light.bg};--lf:${tones.light.fg};--le:${tones.light.border};--db:${tones.dark.bg};--df:${tones.dark.fg};--de:${tones.dark.border}`,
  );
</script>

{#if onpick}
  <button
    type="button"
    class="label-chip pick"
    style={vars}
    title="{full}&#10;Click to filter by this label"
    aria-label="Filter by label {full}"
    tabindex={tabbable ? 0 : -1}
    onclick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onpick(label.name);
    }}><span class="label-name">{label.name}</span></button
  >
{:else}
  <span class="label-chip" style={vars} title={full}><span class="label-name">{label.name}</span></span>
{/if}

<style>
  .label-chip {
    display: inline-flex;
    align-items: center;
    box-sizing: border-box;
    max-width: 22ch;
    height: 18px;
    padding: 0 7px;
    border: 1px solid var(--le);
    border-radius: 30px;
    background: var(--lb);
    color: var(--lf);
    font: 500 11px/16px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    white-space: nowrap;
    vertical-align: middle;
  }
  .label-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  button.label-chip {
    cursor: pointer;
  }
  button.label-chip:hover {
    filter: brightness(0.94);
    text-decoration: underline;
  }
  /* Under the keyboard, the whole name. */
  button.label-chip:focus-visible {
    max-width: none;
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
  @media (prefers-color-scheme: dark) {
    :global(:root:not([data-theme='light'])) .label-chip {
      border-color: var(--de);
      background: var(--db);
      color: var(--df);
    }
    :global(:root:not([data-theme='light'])) button.label-chip:hover {
      filter: brightness(1.15);
    }
  }
  :global(:root[data-theme='dark']) .label-chip {
    border-color: var(--de);
    background: var(--db);
    color: var(--df);
  }
  :global(:root[data-theme='dark']) button.label-chip:hover {
    filter: brightness(1.15);
  }
</style>
